import { useState } from 'react';
import { Handle, Position, useReactFlow } from '@xyflow/react';
import { IconButton, Popover, MenuList, MenuItem, Paper, Typography, CircularProgress, Box, Divider } from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import { StepType } from './utilites/StepType';
import { useParams } from 'react-router-dom';
import useApi from 'hooks/useApi';
import ConditionPopover from './ConditionPopover';
import { createNodeConnectionRule, ConnectionInfo } from './utils/ruleEngineHelper';
import WarningModal from 'utils/warningModel/warningMdel';
 

// Added support for new no-title edge types in TypeScript types
type CustomNodeProps = {
  id: string;
  data: {
    id: string;
    label: string;
    stepNumber?: number;
    onClick: () => void;
    addNodeAndConnect: (id: string, handleId: 'onFailedStep' | 'onCompleteStep' | 'noTitleEdge1' | 'noTitleEdge2', stepType: string, step: any) => void;
    handleUpdateFlow: () => void;
    onDelete?: () => void;
    disabled?: boolean;
    refreshFlow?: () => void;
  };
  selected: boolean;
};

export default function CustomNode({ data, selected }: CustomNodeProps) {
  const { addNewStep, deleteStepByStepUid, checkDeleteDependencies } = useApi();
  const { teamUid } = useParams<{ teamUid: string }>();
  const { getEdges } = useReactFlow();

  const [anchorPosition, setAnchorPosition] = useState<{ top: number; left: number } | null>(null);
  const [activeSource, setActiveSource] = useState<'onFailedStep' | 'onCompleteStep' | 'noTitleEdge1' | 'noTitleEdge2' | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingStep, setLoadingStep] = useState<string | null>(null);
  const [isHovered, setIsHovered] = useState(false);
  const [selectedOption, setSelectedOption] = useState<'components' | 'condition' | null>(null);
  const [conditionDialogOpen, setConditionDialogOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [checkingDependencies, setCheckingDependencies] = useState(false);
  const [stepDeleteHeading, setStepDeleteHeading] = useState('');
  const [stepDeleteSubHeadings, setStepDeleteSubHeadings] = useState<string[]>([]);
  const [stepDeleteHasRules, setStepDeleteHasRules] = useState(false);
  const [stepDeleteMessage, setStepDeleteMessage] = useState<string | undefined>();

  const getUsageNames = (section: any): string[] => {
    if (!section) return [];

    const items = Array.isArray(section?.items)
      ? section.items
      : Array.isArray(section)
      ? section
      : [];

    return items
      .map((item: any) => item?.title || item?.name || item?.uid)
      .filter(Boolean);
  };

  const buildStepDependencyDetails = (dependencyData: any) => {
    const usage = dependencyData?.usage || {};
    const ruleNames = getUsageNames(usage?.rules);
    const notificationNames = getUsageNames(usage?.notifications);
    const alertNames = getUsageNames(usage?.alerts);
    const priorityNames = getUsageNames(usage?.priority);

    const details: string[] = [];

    const pushNumbered = (title: string, items: string[]) => {
      if (!items.length) return;
      details.push(`${title}:`);
      items.forEach((item, index) => {
        details.push(`${index + 1}. ${item}`);
      });
    };

    pushNumbered('Rules', ruleNames);
    pushNumbered('Notifications', notificationNames);
    pushNumbered('Alerts', alertNames);
    pushNumbered('Priority', priorityNames);

    if (!details.length && dependencyData?.message) {
      details.push(dependencyData.message);
    }

    if (!details.length) {
      details.push('Rules: No linked rule names available');
      details.push('Notifications: No linked notification names available');
      details.push('Alerts: No linked alert names available');
      details.push('Priority: No linked priority names available');
    }

    return details;
  };

  // Use step number and title directly from props
  const stepNumber = data.stepNumber;
  const stepTitle = data.label;

  // Check if this node has any outgoing edges from the onCompleteStep handle
  const edges = getEdges();
  const hasCompleteStepChild = edges.some(edge => 
    edge.source === data.id && edge.sourceHandle === 'onCompleteStep'
  );

  const handleClick = (event: React.MouseEvent<HTMLElement>, sourceId: 'onFailedStep' | 'onCompleteStep' | 'noTitleEdge1' | 'noTitleEdge2') => {
    event.stopPropagation();
    const rect = event.currentTarget.getBoundingClientRect();
    setAnchorPosition({
      top: rect.top + rect.height / 2,
      left: rect.right
    });
    setActiveSource(sourceId);
  };

  const handleOptionClick = (option: 'components' | 'condition') => {
    if (option === 'condition') {
      setConditionDialogOpen(true);
      setAnchorPosition(null);
      setActiveSource(null);
      setSelectedOption(null);
    } else {
      setSelectedOption(option);
    }
  };

  const handleBackToOptions = () => {
    setSelectedOption(null);
  };

  const handleItemClick = async (stepType: string) => {
    if (!activeSource || !data.addNodeAndConnect || !teamUid) return;

    setLoading(true);
    setLoadingStep(stepType);

    try {
      const backendNodeKey = activeSource === 'noTitleEdge1' || activeSource === 'noTitleEdge2'
        ? 'onParallelFlow'
        : activeSource;

      console.log(`Creating step with activeSource: ${activeSource}, backendNodeKey: ${backendNodeKey}`);

      const payload = {
        currentNode: {
          title: stepType,
          stepType,
          parents: [data.id],
          dependency: []
        },
        parentNode: {
          nodeKey: backendNodeKey as 'onCompleteStep' | 'onFailedStep' | 'onParallelFlow'
        }
      };

      const newNode = await addNewStep(teamUid, payload as any);

      const newNodeUid = newNode.data?.uid; 
      const parentNodeUid = data.id; 

      data.addNodeAndConnect(data.id, activeSource, stepType, newNode.data);

      if (newNodeUid && parentNodeUid) {
        const connectionInfo: ConnectionInfo = {
          parentNodeUid,
          childNodeUid: newNodeUid,
          connectionType: backendNodeKey as 'onCompleteStep' | 'onFailedStep' | 'onParallelFlow',
          stepType: stepType
        };

        const rulePayload = createNodeConnectionRule(
          connectionInfo,
          data.label,
          `Auto-Rule: ${data.label} -> ${stepType}`
        );

        console.log('Generated rule payload:', rulePayload);
      }

      // Trigger flow update which will save and reload after debounce
      setTimeout(() => {
        data.handleUpdateFlow();
      }, 500);

      console.log('New step created:', newNode);
    } catch (error) {
      console.error('Failed to add step:', error);
    } finally {
      setLoading(false);
      setLoadingStep(null);
      handleClose();
    }
  };

  const handleClose = () => {
    setAnchorPosition(null);
    setActiveSource(null);
    setSelectedOption(null);
  };

  const handleDeleteClick = async () => {
    if (data.disabled) return;

    setStepDeleteHasRules(false);
    setStepDeleteMessage(undefined);

    if (!teamUid || !data.id) {
      setStepDeleteHeading(`"${data.label}" `);
      setStepDeleteSubHeadings([
        'Rules: Related configuration may exist',
        'Notifications: Related configuration may exist',
        'Alerts: Related configuration may exist',
        'Priority: Related configuration may exist'
      ]);
      setStepDeleteHasRules(false);
      setStepDeleteMessage(undefined);
      setDeleteModalOpen(true);
      return;
    }

    setCheckingDependencies(true);
    try {
      const dependencyResponse = await checkDeleteDependencies(teamUid, { stepUid: data.id });
      const dependencyData = dependencyResponse?.data;
      const ruleNames = getUsageNames(dependencyData?.usage?.rules);
      const hasRules = ruleNames.length > 0;

      const stepTitle = dependencyData?.resolved?.stepTitle || data.label;
      setStepDeleteHeading(`"${stepTitle}" want's to delete this step ?`);
      setStepDeleteSubHeadings(buildStepDependencyDetails(dependencyData));
      setStepDeleteHasRules(hasRules);
      setStepDeleteMessage(
        hasRules
          ? 'This step is associated with rule(s). Delete the rule first, then you can delete this step.'
          : undefined
      );
    } catch (error) {
      console.error('Failed to load step dependencies for delete modal:', error);
      setStepDeleteHeading(`"${data.label}"  want's to delete this step ? `);
      setStepDeleteSubHeadings([
        'Rules: Related configuration may exist',
        'Notifications: Related configuration may exist',
        'Alerts: Related configuration may exist',
        'Priority: Related configuration may exist'
      ]);
      setStepDeleteHasRules(false);
      setStepDeleteMessage(undefined);
    } finally {
      setCheckingDependencies(false);
      setDeleteModalOpen(true);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!teamUid || !data.id) {
      console.error('Missing team UID or step UID');
      return;
    }

    if (stepDeleteHasRules) {
      return;
    }

    setDeleting(true);
    try {
       
      
      const deleteResponse = await deleteStepByStepUid(teamUid, data.id);
      
      console.log('Step deleted and reconnected successfully', deleteResponse);

      // Close modal only on success
      setDeleteModalOpen(false);

      // Reload the entire component using refreshFlow
    if (data.refreshFlow) {
      data.refreshFlow();
    }
     
      
    } catch (error) {
      console.error('Failed to delete step:', error);
      // Keep modal open on error (don't close it)
       // Reload the component even on failure to ensure UI is in sync
    if (data.refreshFlow) {
      data.refreshFlow();
    }
    } finally {
      // Only reset deleting state
      setDeleting(false);
    }
  };

 

  return (
    <div
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{ position: 'relative', display: 'inline-block' }}
    >
      <div
        onClick={data.onClick}
        style={{
          padding: 10,
          color: 'black',
          background: selected ? '#e3f2fd' : '#fff',
          border: '2px solid',
          borderColor: selected ? '#2196f3' : '#ddd',
          borderRadius: 4,
          position: 'relative',
          width: 160
        }}
      >
        <Handle type="target" position={Position.Top} style={{ background: 'blue' }} />
        <Handle type="target" position={Position.Left} id="loopBackTarget" style={{ background: '#ff9800', width: 10, height: 10, top: '70%' }} />
        {/* <Handle type="target" position={Position.Left} style={{ background: 'blue' }} /> */}

        {/* Step Number Badge */}
        {stepNumber && (
          <Box
            sx={{
              position: 'absolute',
              top: -12,
              left: -12,
              width: 28,
              height: 28,
              backgroundColor: '#1976d2',
              color: 'white',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '14px',
              fontWeight: 'bold',
              border: '2px solid white',
              boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
              zIndex: 5
            }}
          >
            {stepNumber}
          </Box>
        )}

        <Typography variant="subtitle1" fontWeight="bold" align="center" 
        sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }} >
          {stepTitle}
        </Typography>
        
        <Handle type="source" position={Position.Left} id="loopBackHandle" style={{ background: '#ff9800', width: 10, height: 10 }} />
        <Handle type="source" position={Position.Right} id="onCompleteStep" style={{ background: 'green' }} />
        {/* onFailedStep Handle - hidden but kept in DOM for existing edges to render properly */}
        <Handle type="source" position={Position.Bottom} id="onFailedStep" style={{ background: 'red', opacity: 0, width: 1, height: 1 }} />
      </div>

      {isHovered && (
        <>
          <div
            style={{
              position: 'absolute',
              top: -15,
              right: -10,
              zIndex: 10
            }}
          >
            <IconButton
              size="small"
              onClick={() => {
                void handleDeleteClick();
              }}
              color="error"
              disabled={data.disabled || checkingDependencies}

              



            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </div>

          <div
            style={{
              position: 'absolute',
              top: '50%',
              right: -35,
              transform: 'translateY(-50%)',
              zIndex: 10
            }}
          >
            <IconButton
              size="medium"
              onClick={(e) => handleClick(e, 'onCompleteStep')}
              color="success"
              disabled={hasCompleteStepChild}
              sx={hasCompleteStepChild ? { opacity: 0.5 } : {}}
            >
              <AddCircleOutlineIcon fontSize="small" />
            </IconButton>
          </div>

          {/* onFailedStep button removed - feature disabled */}
          
        </>
      )}

      <Popover
        open={Boolean(anchorPosition) && selectedOption !== 'condition'}
        anchorReference="anchorPosition"
        anchorPosition={anchorPosition || undefined}
        onClose={handleClose}
        transformOrigin={{
          vertical: 'center',
          horizontal: 'left'
        }}
        PaperProps={{
          sx: {
            minWidth: 200,
            maxWidth: 280,
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            border: '1px solid rgba(0,0,0,0.08)'
          }
        }}
      >
        <Paper elevation={0}>
          {selectedOption === null ? (
            <Box sx={{ p: 1 }}>
              <Typography variant="subtitle2" sx={{ px: 2, py: 1, color: 'text.secondary', fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Add New Step
              </Typography>
              <Divider sx={{ mb: 1 }} />
              <MenuList dense sx={{ py: 0 }}>
                <MenuItem
                  onClick={() => handleOptionClick('components')}
                  sx={{
                    borderRadius: 1,
                    mx: 1,
                    mb: 0.5,
                    '&:hover': {
                      backgroundColor: 'primary.50',
                      color: 'primary.main'
                    }
                  }}
                >
                  <Typography sx={{ fontWeight: 500 }}>Components</Typography>
                </MenuItem>
                <MenuItem
                  onClick={() => handleOptionClick('condition')}
                  sx={{
                    borderRadius: 1,
                    mx: 1,
                    '&:hover': {
                      backgroundColor: 'primary.50',
                      color: 'primary.main'
                    }
                  }}
                >
                  <Typography sx={{ fontWeight: 500 }}>Condition</Typography>
                </MenuItem>
              </MenuList>
            </Box>
          ) : selectedOption === 'components' ? (
            <Box sx={{ p: 1 }}>
              <MenuItem
                onClick={handleBackToOptions}
                sx={{
                  borderRadius: 1,
                  mx: 1,
                  mb: 1,
                  backgroundColor: 'grey.50',
                  '&:hover': {
                    backgroundColor: 'grey.100'
                  }
                }}
              >
                <ArrowBackIcon sx={{ fontSize: 18, mr: 1, color: 'primary.main' }} />
                <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 500 }}>Back</Typography>
              </MenuItem>
              <Divider sx={{ mb: 1 }} />
              <MenuList dense sx={{ py: 0 }}>
                {Object.values(StepType).map((step) => (
                  <MenuItem
                    key={step}
                    onClick={() => handleItemClick(step)}
                    disabled={loading}
                    sx={{
                      borderRadius: 1,
                      mx: 1,
                      mb: 0.5,
                      '&:hover': {
                        backgroundColor: 'primary.50',
                        color: 'primary.main'
                      },
                      '&.Mui-disabled': {
                        opacity: 0.6
                      }
                    }}
                  >
                    {loading && loadingStep === step ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CircularProgress size={16} />
                        <Typography sx={{ fontWeight: 500 }}>{step}</Typography>
                      </Box>
                    ) : (
                      <Typography sx={{ fontWeight: 500 }}>{step}</Typography>
                    )}
                  </MenuItem>
                ))}
              </MenuList>
            </Box>
          ) : null}
        </Paper>
      </Popover>

      <ConditionPopover
        open={conditionDialogOpen}
        onClose={() => setConditionDialogOpen(false)}
        addNodeAndConnect={data.addNodeAndConnect}
        handleUpdateFlow={data.handleUpdateFlow}
        selectedNode={data}
      />

      <WarningModal
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title="Deleting Step ?"
        heading={stepDeleteHeading || ` Want's to delete this step ? "${data.label}" `} 
        message={stepDeleteMessage}
        subHeadings={stepDeleteSubHeadings}
        type="delete"
        confirmText="Delete Step"
        cancelText="Cancel"
        onConfirm={handleDeleteConfirm}
        confirmDisabled={stepDeleteHasRules}
        loading={deleting}
        showCancelButton={true}
      />
    </div>
  );
}

 
 

 